import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined'
import IconButton from '@mui/material/IconButton'
import { useTheme } from '@mui/material/styles'
import { Key } from '@styled-icons/heroicons-outline/Key'
import { Login } from '@styled-icons/heroicons-outline/Login'
import React, { useCallback, useMemo, useState } from 'react'

import { useAuthValue } from 'auth/AuthContext'
import ChangePassword from 'components/change-password/ChangePassword'
import Constants from 'Constants'
import Endpoint from 'middleware/sagas/endpoint'
import { handleFirebaseLogout } from 'providers/firebaseLogout'
import { isEmailProvider } from 'settings/constants/auth'
import T from 'T'
import { decodeToken } from 'utils/token'

import AvatarSquare from './AvatarSquare'
import UserMenu, { UserMenuItem } from './UserMenu'

import './User.scss'

const { profilePic, profilePicUri } = Endpoint.user
const { PRIVACY_POLICY_PAGE } = Constants

interface AuthValue {
  currentUser?: { accessToken?: string }
}

interface DecodedToken {
  // eslint-disable-next-line camelcase
  firebase?: { sign_in_provider?: string }
}

interface UserProps {
  firstName?: string
  lastName?: string
  profilePicture?: string
  enableClick?: boolean
  uri?: string
  showPrivacyPolicy?: boolean
}

const User = ({
  firstName = '',
  lastName = '',
  profilePicture = '',
  enableClick = true,
  uri = '',
  showPrivacyPolicy = true,
}: UserProps) => {
  const theme = useTheme()
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  // currentUser.providerData returns all strategies, so decoding the token to get the strategy used
  const token = (useAuthValue() as AuthValue | undefined)?.currentUser?.accessToken
  const decodedToken = decodeToken(token) as DecodedToken | null
  const isPasswordProvider = isEmailProvider(decodedToken?.firebase?.sign_in_provider)

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (enableClick) setAnchorEl(event.currentTarget)
    },
    [enableClick]
  )

  const handleCloseMenu = useCallback(() => setAnchorEl(null), [])

  const handleOpenChangePassword = useCallback(() => setIsChangePasswordOpen(true), [])

  const handleCloseChangePassword = useCallback(() => setIsChangePasswordOpen(false), [])

  const menuItems = useMemo<UserMenuItem[]>(
    () => [
      ...(isPasswordProvider ? [{ title: T.CHANGE_PASSWORD, icon: <Key />, onClick: handleOpenChangePassword }] : []),
      ...(showPrivacyPolicy ? [{ title: T.PRIVACY_POLICY, icon: <PolicyOutlinedIcon />, url: PRIVACY_POLICY_PAGE }] : []),
      { title: T.LOGOUT, icon: <Login />, onClick: handleFirebaseLogout },
    ],
    [isPasswordProvider, showPrivacyPolicy, handleOpenChangePassword]
  )

  const profileUri = uri ? profilePicUri + uri : profilePic

  return (
    <>
      <IconButton size="large" onClick={handleOpenMenu}>
        <AvatarSquare
          firstName={firstName}
          lastName={lastName}
          src={profilePicture ? profileUri : undefined}
          sx={{ ...theme.typography.h5, width: 32, height: 32 }}
        />
      </IconButton>
      <ChangePassword passwordModal={isChangePasswordOpen} onClose={handleCloseChangePassword} />
      <UserMenu anchorEl={anchorEl} items={menuItems} onClose={handleCloseMenu} />
    </>
  )
}

export default User
